import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail, MapPin } from 'lucide-react';

const Layout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  const menuItems = [
    {
      label: "Privat",
      href: "#",
      children: [
        { label: "Privatøkonomisk rådgivning", href: "/oekonomisk-raadgivning" },
        { label: "Økonomi overblik", href: "/oekonomi-overblik" },
        { label: "Pensionsrådgivning", href: "/pensionsraadgivning" },
        { label: "Rådgivning til boligøkonomi", href: "/bolig-raadgivning" },
        { label: "Investeringsrådgivning til private", href: "/investering" }
      ]
    },
    {
      label: "Erhverv",
      href: "#",
      children: [
        { label: "Erhvervsrådgivning", href: "/erhvervsraadgivning" },
        { label: "Investeringsrådgivning", href: "/investeringsraadgivning" }
      ]
    },
    { label: "Foredrag", href: "/foredrag" },
    { label: "Priser for økonomisk rådgivning og bistand", href: "/priser" },
    { label: "YouTube", href: "/youtube" },
    { label: "Nyhedsbrev", href: "/nyhedsbrev" },
    { label: "Økonomiblog", href: "/blog" },
    { label: "Om os", href: "/om-os" },
    {
      label: "Kontakt os",
      href: "/kontakt-os",
      children: [
        { label: "GDPR", href: "/gdpr" }
      ]
    }
  ];

  const toggleDropdown = (index) => {
    if (activeDropdown === index) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(index);
    }
  };

  return (
    <div className="min-h-screen flex flex-col font-sans text-slate-900">
      {/* Top Bar */}
      <div className="bg-slate-900 text-white py-2 px-4 hidden md:block">
        <div className="container mx-auto flex justify-end items-center text-sm">
          <div className="flex space-x-6">
            <a href="tel:51969064" className="flex items-center hover:text-[#b60f00] transition-colors">
              <Phone size={14} className="mr-2" /> 51 96 90 64 (Sjælland/Fyn)
            </a>
            <a href="tel:50882222" className="flex items-center hover:text-[#b60f00] transition-colors">
              <Phone size={14} className="mr-2" /> 50 88 22 22 (Vestjylland/Skive)
            </a>
            <a href="tel:51969064" className="flex items-center hover:text-[#b60f00] transition-colors">
              <Phone size={14} className="mr-2" /> 51 96 90 64 (Jylland)
            </a>
            <a href="mailto:info@finansoptimering.dk" className="flex items-center hover:text-[#b60f00] transition-colors">
              <Mail size={14} className="mr-2" /> info@finansoptimering.dk
            </a>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center">
              <img 
                src="https://assets.auxodev.dk/assets/3ae65674-a84a-41bf-9a95-918b20317861/f1048a6e-cc93-4405-b11c-c88cdb426e22.png" 
                alt="Finansoptimering" 
                className="h-20 w-auto brightness-0 hover:scale-105 transition-transform duration-300"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden min-[1601px]:flex items-center space-x-1">
              {menuItems.map((item, index) => {
                const isParentActive = item.children?.some(child => location.pathname === child.href);
                const isActive = location.pathname === item.href || isParentActive;

                return (
                  <div key={index} className="relative group">
                    {item.children ? (
                      <div className="relative">
                        <button 
                          className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                            isActive
                              ? 'text-[#b60f00] bg-red-50' 
                              : 'text-slate-700 hover:text-[#b60f00] hover:bg-slate-50'
                          }`}
                          onClick={() => toggleDropdown(index)}
                        >
                          {item.label}
                          <ChevronDown size={14} className="ml-1" />
                        </button>
                        <div className="absolute left-0 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                          <div className="py-2">
                            {item.children.map((child, childIndex) => (
                              <Link
                                key={childIndex}
                                to={child.href}
                                className={`block px-4 py-2 text-sm transition-colors ${
                                  location.pathname === child.href
                                    ? 'text-[#b60f00] bg-red-50 font-semibold'
                                    : 'text-slate-700 hover:bg-red-50 hover:text-[#b60f00]'
                                }`}
                              >
                                {child.label}
                              </Link>
                            ))}
                            {item.href !== '#' && (
                              <Link
                                to={item.href}
                                className={`block px-4 py-2 text-sm font-semibold border-t border-slate-100 mt-1 transition-colors ${
                                  location.pathname === item.href
                                    ? 'text-[#b60f00] bg-red-50'
                                    : 'text-[#b60f00] hover:bg-red-50'
                                }`}
                              >
                                Gå til {item.label}
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                          location.pathname === item.href 
                            ? 'text-[#b60f00] bg-red-50' 
                            : 'text-slate-700 hover:text-[#b60f00] hover:bg-slate-50'
                        }`}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                );
              })}
              <Link 
                to="/kontakt-os" 
                className="ml-4 bg-[#b60f00] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#a00d00] transition-all shadow-md hover:shadow-lg active:scale-95"
              >
                Book gratis møde
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="min-[1601px]:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-md"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="min-[1601px]:hidden bg-white border-t border-slate-200 max-h-[calc(100vh-80px)] overflow-y-auto">
            <div className="px-4 py-6 space-y-1">
              {menuItems.map((item, index) => {
                const isParentActive = item.children?.some(child => location.pathname === child.href);
                const isActive = location.pathname === item.href || isParentActive;

                return (
                  <div key={index} className="space-y-1">
                    {item.children ? (
                      <>
                        <button 
                          className={`w-full flex justify-between items-center px-4 py-3 text-base font-semibold rounded-lg transition-colors ${
                            isActive ? 'text-[#b60f00] bg-red-50' : 'text-slate-800 hover:bg-slate-50'
                          }`}
                          onClick={() => toggleDropdown(index)}
                        >
                          {item.label}
                          <ChevronDown size={18} className={`transition-transform duration-200 ${activeDropdown === index ? 'rotate-180' : ''}`} />
                        </button>
                        {activeDropdown === index && (
                          <div className="pl-4 space-y-1 bg-slate-50 rounded-lg py-2">
                            {item.children.map((child, childIndex) => (
                              <Link
                                key={childIndex}
                                to={child.href}
                                className={`block px-4 py-3 text-sm transition-colors ${
                                  location.pathname === child.href 
                                    ? 'text-[#b60f00] font-bold' 
                                    : 'text-slate-600 hover:text-[#b60f00]'
                                }`}
                                onClick={() => setIsMenuOpen(false)}
                              >
                                {child.label}
                              </Link>
                            ))}
                            {item.href !== '#' && (
                              <Link
                                to={item.href}
                                className={`block px-4 py-3 text-sm font-bold transition-colors ${
                                  location.pathname === item.href ? 'text-[#b60f00]' : 'text-[#b60f00]'
                                }`}
                                onClick={() => setIsMenuOpen(false)}
                              >
                                Gå til {item.label}
                              </Link>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <Link
                        to={item.href}
                        className={`block px-4 py-3 text-base font-semibold rounded-lg transition-colors ${
                          location.pathname === item.href 
                            ? 'text-[#b60f00] bg-red-50' 
                            : 'text-slate-800 hover:bg-slate-50'
                        }`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                );
              })}
              <div className="pt-4">
                <Link 
                  to="/kontakt-os" 
                  className="block w-full bg-[#b60f00] text-white text-center px-4 py-4 rounded-xl font-bold hover:bg-[#a00d00] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Book gratis møde
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 pt-16 pb-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img 
                src="https://assets.auxodev.dk/assets/3ae65674-a84a-41bf-9a95-918b20317861/f1048a6e-cc93-4405-b11c-c88cdb426e22.png" 
                alt="Finansoptimering" 
                className="h-[85px] w-auto hover:scale-105 transition-transform duration-300"
              />
              <p className="text-sm leading-relaxed">
                Uvildig økonomisk rådgivning til private og erhverv. Vi hjælper dig med at optimere din økonomi, minimere omkostninger og sikre dig det bedste afkast.
              </p>
              <div className="flex space-x-4">
                {/* Social links could go here */}
              </div>
            </div>

            <div>
              <h4 className="text-white font-bold mb-6">Privat</h4>
              <ul className="space-y-4 text-sm">
                <li><Link to="/oekonomisk-raadgivning" className="hover:text-[#b60f00] transition-colors">Privatøkonomisk rådgivning</Link></li>
                <li><Link to="/oekonomi-overblik" className="hover:text-[#b60f00] transition-colors">Økonomi overblik</Link></li>
                <li><Link to="/pensionsraadgivning" className="hover:text-[#b60f00] transition-colors">Pensionsrådgivning</Link></li>
                <li><Link to="/bolig-raadgivning" className="hover:text-[#b60f00] transition-colors">Rådgivning til boligøkonomi</Link></li>
                <li><Link to="/investering" className="hover:text-[#b60f00] transition-colors">Investeringsrådgivning</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-6">Erhverv</h4>
              <ul className="space-y-4 text-sm">
                <li><Link to="/erhvervsraadgivning" className="hover:text-[#b60f00] transition-colors">Erhvervsrådgivning</Link></li>
                <li><Link to="/investeringsraadgivning" className="hover:text-[#b60f00] transition-colors">Investeringsrådgivning</Link></li>
                <li><Link to="/foredrag" className="hover:text-[#b60f00] transition-colors">Foredrag</Link></li>
                <li><Link to="/blog" className="hover:text-[#b60f00] transition-colors">Økonomiblog</Link></li>
              </ul>
            </div>

          </div>

          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center text-xs">
            <p>&copy; {new Date().getFullYear()} Finansoptimering ApS. Alle rettigheder forbeholdes.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link to="/gdpr" className="hover:text-[#b60f00] transition-colors">GDPR & Privatlivspolitik</Link>
              <a href="#" className="hover:text-[#b60f00] transition-colors">Cookiepolitik</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
